// Copy for the benefits-flow prototype. Presentation-free strings only; the
// question copy lives in screener/questions.ts, benefit names/values in
// engine/benefits.ts, and mock data would live in a fixtures module.

import type { ActionState } from '@/benefits-flow/engine/types';

export type ModeId = 'quiz' | 'tree' | 'compare';

export const MODES: { id: ModeId; label: string; blurb: string }[] = [
  {
    id: 'quiz',
    label: 'The quiz',
    blurb: 'Triage, then the minimum questions — benefits light up as you go.',
  },
  {
    id: 'tree',
    label: 'Decision tree',
    blurb: 'The whole tree, and every distinct outcome the branches lead to.',
  },
  {
    id: 'compare',
    label: 'Compare designs',
    blurb: 'The same reveal rendered four ways, side by side.',
  },
];

export const HEADER = {
  title: 'Benefits flow',
  subtitle: 'Who are you caring for? → what they can claim → how we collect it',
};

// Action model (brief §8). Each state has a label, a one-line meaning, and a
// tone that maps to a chip colour in the UI (no colours here).
export const ACTIONS: Record<
  ActionState,
  {
    label: string;
    meaning: string;
    tone: 'ready' | 'wait' | 'guide' | 'check' | 'info' | 'unknown';
  }
> = {
  APPLY_NOW: {
    label: 'Apply now',
    meaning: 'We hold the form and can complete it with you.',
    tone: 'ready',
  },
  BLOCKED: {
    label: 'Waiting on an award',
    meaning: 'Eligible, but not yet claimable until another award lands.',
    tone: 'wait',
  },
  GUIDED: {
    label: 'We’ll guide you',
    meaning: 'No national form — we help you do it yourself.',
    tone: 'guide',
  },
  CHECK_AWARD: {
    label: 'Check your award',
    meaning: 'We need a detail off a document you already hold.',
    tone: 'check',
  },
  INFO_ONLY: {
    label: 'Good to know',
    meaning: 'Nothing for us to submit — apply to the council directly.',
    tone: 'info',
  },
  MORE_INFO_NEEDED: {
    label: 'More info needed',
    meaning: 'The screener didn’t collect enough to decide.',
    tone: 'unknown',
  },
};

export const ELIGIBILITY_COPY = {
  eligible: 'Likely eligible',
  ineligible: 'Not eligible',
  'more-info-needed': 'Not enough info yet',
} as const;

export const REVEAL = {
  interimHeading: 'Already worth a look',
  interimLead: 'From your first three answers, {name} could claim',
  interimContinue: 'Keep going',
  finalHeading: 'Here’s what {name} could claim',
  finalLead: 'Based on everything you told me',
  perYear: 'a year',
  estimateNote: 'These are estimated ranges, not calculated figures.',
  nothingYet: 'Nothing to show yet — a few more answers will change that.',
};

// Design-variant annotations (brief §10) — what each optimises for and hides.
export const VARIANTS: {
  id: 'A' | 'B' | 'C' | 'D';
  name: string;
  optimises: string;
  hides: string;
}[] = [
  {
    id: 'A',
    name: 'Value-ranked list',
    optimises: 'Maximum perceived value — every benefit, biggest number first.',
    hides: 'That the biggest number might be one they can’t act on yet.',
  },
  {
    id: 'B',
    name: 'Single next best action',
    optimises: 'Minimum overwhelm — one thing to do now, the rest collapsed.',
    hides: 'The total value, which is the thing that converts.',
  },
  {
    id: 'C',
    name: 'Grouped by what you can do',
    optimises: 'Honesty about the dependency chain — ready, waiting, guided.',
    hides:
      'Nothing much, but it’s the densest, and “waiting” can read as a fob-off.',
  },
  {
    id: 'D',
    name: 'Sequence view',
    optimises: 'Setting expectations — what unlocks what, and in what order.',
    hides: 'Speed — a timeline can make the whole thing look slow.',
  },
];

export const SCENARIOS = {
  heading: 'Every distinct outcome',
  computing: 'Computing scenarios…',
  distinctLead: 'distinct outcomes',
  rawLead:
    'raw input combinations across the modelled dimensions collapse into',
  reductionNote:
    'Dimensions are enumerated over outcome-distinct representative values (e.g. 6-month and 12-month durations collapse — they satisfy the same rule). The distinct-outcome count is exact; the raw count is over this reduced grid, not the full ~15M input space.',
  openWalk: 'Open in Walk it',
  columns: {
    scenario: 'Representative scenario',
    eligible: 'Eligible',
    value: 'Est. value',
    actions: 'Action states',
    collapses: 'Raw combos',
  },
  filterNation: 'Nation',
  filterAction: 'Has action',
  filterAll: 'All',
};

export const DEV_PANEL = {
  title: 'Dev panel',
  screenerOrder: 'Screener order',
  orderReordered: 'Reordered',
  orderProduction: 'Production',
  inputMode: 'Income & savings',
  inputPrecise: 'Precise',
  inputBands: 'Bands',
  awardSimulator: 'Award simulator',
  awardHint: 'Mark a benefit awarded and watch blocked ones unblock.',
  ruleTrace: 'Rule trace',
  ruleTraceHint:
    'Which rule fired per benefit, its key, and the facts it read. Production omits RuleKey from its API — this shows it should expose it.',
  discrepancies: 'Discrepancies firing now',
  noDiscrepancies: 'No known bugs fired for this scenario.',
  firedRuleNone: 'no rule fired (not enough info)',
};

export const STUB = {
  heading: 'This is where the form would start',
  questionsLabel: 'questions',
  sectionsLabel: 'Sections',
  timeLabel: 'How long',
  note: 'The prototype stubs the destination — it doesn’t build the form.',
  carryHeading: 'What could carry over',
  carryBody:
    'The screener and the form use separate question ID namespaces, so today nothing pre-fills. These answers could:',
};
